import moment from 'moment';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useRepair} from '../context/repair-context';
import {useTime} from '../context/time-context';
import {daySelected} from '../features/scheduling/schedulerSlice';
import {retrieveSchedulableWindows} from '../utils/scheduled-window';
import CalendarLayout from './calendar-layout';
import MobileCalendarLayout from './mobile-calendar-layout';
import WindowSelectionLayout from './window-selection-layout';

const shouldShowMobileLayout = (width, breakpointMobile) => width <= breakpointMobile;

const AppointmentPicker = (props) => {
  const dispatch = useDispatch();
  const {dynamicContent, repair} = useRepair();
  const {windows, precise: preciseTimes, maximumDay, fetchingWindows} = useTime();
  const {noTimesAvailableMessage, noTechniciansFoundMessage} = dynamicContent;
  const [renderCalendar, setRenderCalendar] = useState(false);
  const [showMobileLayout, setShowMobileLayout] = useState(shouldShowMobileLayout(window.innerWidth, 576));
  const scheduler = useSelector((state) => state.scheduler);
  const {selectedDay} = scheduler;
  const minDate = moment(scheduler.minimumDay);
  const maxDate = moment(maximumDay);
  const schedulableWindows = windows ? convertWindowsToMoments(windows) : {};

  useEffect(() => {
    const handleScreenResize = () => {
      const breakpointMobile = 576;
      if (!showMobileLayout && window.innerWidth <= breakpointMobile) {
        setShowMobileLayout(true);
      } else if (showMobileLayout && window.innerWidth > breakpointMobile) {
        setShowMobileLayout(false);
      }
    };

    window.addEventListener('resize', handleScreenResize);
    return () => {
      window.removeEventListener('resize', handleScreenResize);
    };
  }, [showMobileLayout]);

  const toggleCalendar = () => {
    setRenderCalendar((prevState) => !prevState);
  };

  const handleCalendarSelect = (day) => {
    handleSelectDay(day);
    toggleCalendar();
  };

  const handleSelectDay = (day) => dispatch(daySelected({day: day.toISOString(), needToFetchMoreTimes: false}));

  const {decrementMonth, decrementWeek, incrementMonth, incrementWeek, validatingWindow, style} = props;
  const {timeZone} = repair;
  let noSchedulableWindows = Object.keys(schedulableWindows).length === 0;
  const selectedDayMoment = moment(selectedDay);

  let arrowParams = {
    leftDisabled: fetchingWindows || selectedDayMoment.isSameOrBefore(minDate, 'month') || noSchedulableWindows,
    onLeft: decrementMonth,
    onRight: incrementMonth,
    rightDisabled: fetchingWindows || selectedDayMoment.isSameOrAfter(maxDate, 'week') || noSchedulableWindows,
  };

  let dateSelectParams = {
    isLoading: fetchingWindows,
    selectDay: handleCalendarSelect,
    minDate,
    maxDate,
    schedulableWindows,
    selectedDay: selectedDayMoment,
    timeZone,
  };

  if (renderCalendar && showMobileLayout) {
    return <MobileCalendarLayout arrowParams={arrowParams} dateSelectParams={dateSelectParams} />;
  } else if (renderCalendar && !showMobileLayout) {
    return <CalendarLayout arrowParams={arrowParams} dateSelectParams={dateSelectParams} />;
  } else {
    dateSelectParams = {
      ...dateSelectParams,
      selectDay: handleSelectDay,
    };
    arrowParams = {
      ...arrowParams,
      leftDisabled: fetchingWindows || selectedDayMoment.isSameOrBefore(minDate, 'week') || noSchedulableWindows,
      onLeft: decrementWeek,
      onRight: incrementWeek,
    };

    return (
      <WindowSelectionLayout
        arrowParams={arrowParams}
        dateSelectParams={dateSelectParams}
        maxDate={maxDate}
        noSchedulableWindows={noSchedulableWindows}
        noTechniciansFoundMessage={noTechniciansFoundMessage}
        noTimesAvailableMessage={noTimesAvailableMessage}
        onCalendarToggle={toggleCalendar}
        preciseTimes={preciseTimes}
        selectedDayWindows={retrieveSchedulableWindows(schedulableWindows, selectedDayMoment)}
        style={style}
        validatingWindow={validatingWindow}
      />
    );
  }

  function convertWindowsToMoments(windows) {
    return Object.keys(windows).reduce((acc, day) => {
      acc[day] = windows[day].map((window) => {
        const {start, end, ...rest} = window;
        return {
          start: moment(start),
          end: moment(end),
          ...rest,
        };
      });
      return acc;
    }, {});
  }
};

export default AppointmentPicker;
